import React, { useEffect, useRef, useState } from 'react'

// how long the stagger delays stay on the children before hover transitions run undelayed
const SETTLE_MS = 1800

export const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches

/**
 * Marks an element as revealed once it scrolls into view, as the prototype's .reveal and .stagger.
 * Spread the result on the element; PageRoot's styles hide it until `data-in` lands.
 *
 * @param stagger   the children fade in one after the other instead of the element as a whole
 * @param onReveal  called once, when the element first shows
 */
export const useRevealProps = ({ stagger = false, onReveal } = {}) => {
  const ref = useRef(null)
  const [shown, setShown] = useState(false)
  const [settled, setSettled] = useState(false)

  useEffect(() => {
    const element = ref.current
    if (!element || shown) return undefined

    const reveal = () => setShown(true)
    if (!('IntersectionObserver' in window)) {
      reveal()
      return undefined
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          reveal()
          observer.disconnect()
        }
      },
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' },
    )
    observer.observe(element)

    // the prototype's safety net: anything on screen after 2.5 seconds shows regardless
    const timer = setTimeout(() => {
      if (element.getBoundingClientRect().top < window.innerHeight) reveal()
    }, 2500)

    return () => {
      observer.disconnect()
      clearTimeout(timer)
    }
  }, [shown])

  useEffect(() => {
    if (!shown) return undefined
    if (onReveal) onReveal()
    const timer = setTimeout(() => setSettled(true), SETTLE_MS)
    return () => clearTimeout(timer)
  }, [shown]) // eslint-disable-line react-hooks/exhaustive-deps

  return {
    ref,
    'data-reveal': stagger ? undefined : '',
    'data-stagger': stagger ? '' : undefined,
    'data-in': shown ? '' : undefined,
    'data-settled': settled ? '' : undefined,
  }
}

/** A div that reveals itself; for a styled element use the hook directly. */
const Reveal = ({ stagger, onReveal, children, ...rest }) => (
  <div {...useRevealProps({ stagger, onReveal })} {...rest}>
    {children}
  </div>
)

export default Reveal
